/**
 * Audio format detection from magic bytes.
 * Shared between the standalone NLE export pipeline and the embedded export path.
 */

/** Detected audio format info */
export interface AudioFormat {
  ext: string;
  channels: number; // 1 = mono, 2 = stereo
}

/** Detect audio format + channel count from magic bytes */
export function detectAudioFormat(bytes: Uint8Array, mimeType?: string): AudioFormat {
  // MP3: starts with ID3 tag or MPEG sync word
  if (
    (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) || // "ID3"
    (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0) // MPEG sync
  ) {
    return { ext: 'mp3', channels: detectMp3Channels(bytes) };
  }
  // WAV: starts with "RIFF"
  if (bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46) {
    return { ext: 'wav', channels: detectWavChannels(bytes) };
  }
  // OGG: "OggS"
  if (bytes[0] === 0x4F && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
    return { ext: 'ogg', channels: 2 };
  }
  // M4A/AAC: ftyp box (common from iPhone voice memos, imported audio)
  if (bytes[4] === 0x66 && bytes[5] === 0x74 && bytes[6] === 0x79 && bytes[7] === 0x70) {
    return { ext: 'm4a', channels: 2 }; // safe default; NLEs handle stereo fine for mono sources
  }
  // FLAC: "fLaC"
  if (bytes[0] === 0x66 && bytes[1] === 0x4C && bytes[2] === 0x61 && bytes[3] === 0x43) {
    return { ext: 'flac', channels: 2 };
  }
  // Fall back to mimeType
  if (mimeType?.includes('mpeg') || mimeType?.includes('mp3')) return { ext: 'mp3', channels: 2 };
  if (mimeType?.includes('mp4') || mimeType?.includes('aac') || mimeType?.includes('m4a')) return { ext: 'm4a', channels: 2 };
  if (mimeType?.includes('ogg')) return { ext: 'ogg', channels: 2 };
  if (mimeType?.includes('flac')) return { ext: 'flac', channels: 2 };
  return { ext: 'wav', channels: 1 };
}

/** Read channel mode from first MPEG frame header (after ID3 tag if present) */
function detectMp3Channels(bytes: Uint8Array): number {
  let offset = 0;
  // Skip ID3 tag if present
  if (bytes[0] === 0x49 && bytes[1] === 0x44 && bytes[2] === 0x33) {
    // ID3v2 size is 4 bytes at offset 6, syncsafe integer
    const size = ((bytes[6] & 0x7F) << 21) | ((bytes[7] & 0x7F) << 14) |
                 ((bytes[8] & 0x7F) << 7) | (bytes[9] & 0x7F);
    offset = 10 + size;
  }
  // Find MPEG sync word
  for (let i = offset; i < Math.min(bytes.length - 4, offset + 2048); i++) {
    if (bytes[i] === 0xFF && (bytes[i + 1] & 0xE0) === 0xE0) {
      // Channel mode is in bits 6-7 of byte 3: 11 = mono, anything else = stereo
      const channelMode = (bytes[i + 3] >> 6) & 0x03;
      return channelMode === 3 ? 1 : 2;
    }
  }
  return 2; // default stereo
}

/** Read channel count from WAV fmt chunk */
function detectWavChannels(bytes: Uint8Array): number {
  // "fmt " chunk starts at offset 12, channels at fmt+10 (little-endian uint16)
  // But fmt chunk may not be at exactly offset 12, so search for it
  for (let i = 12; i < Math.min(bytes.length - 10, 512); i++) {
    if (bytes[i] === 0x66 && bytes[i + 1] === 0x6D && bytes[i + 2] === 0x74 && bytes[i + 3] === 0x20) {
      // "fmt " found, channels at offset +10 from chunk start
      return bytes[i + 10] | (bytes[i + 11] << 8);
    }
  }
  return 1; // default mono for WAV (TTS output)
}
